// src/utils/api.js
// Hardened API helper: CRA/Vite friendly, cookie auth, safe redirects, robust timeouts/retries.

const VITE = (typeof import.meta !== "undefined" && import.meta.env) ? import.meta.env : {};
const CRA  = (typeof process !== "undefined" && process.env) ? process.env : {};

function envGet(keys, fallback = "") {
  for (const k of keys) {
    const v1 = VITE[`VITE_${k}`];
    if (v1 !== undefined && v1 !== "") return String(v1);
    const v2 = CRA[`REACT_APP_${k}`];
    if (v2 !== undefined && v2 !== "") return String(v2);
  }
  return fallback;
}

/* ------------------------- Configurable base + login ------------------------ */
// 1) Read configured base (can be empty => same-origin)
let API_BASE = (envGet(["API_BASE", "API_BASE_URL"], "").trim().replace(/\/$/, "")) || "";

// 2) In production, if VITE_API_BASE_URL equals current origin, force same-origin to avoid CORS entirely
try {
  if (typeof window !== "undefined") {
    const configured = (VITE?.VITE_API_BASE_URL || "").trim().replace(/\/$/, "");
    const currentOrigin = window.location.origin.replace(/\/$/, "");
    if (configured && configured === currentOrigin) {
      API_BASE = ""; // relative requests => same-origin (no CORS/preflight)
    }
  }
} catch { /* no-op */ }

// Admin login path only (public users should never be redirected)
let LOGIN_PATH = envGet(["LOGIN_PATH"], "/admin/login");

export function setApiBase(url) {
  API_BASE = String(url || "").trim().replace(/\/$/, "");
}
export function getApiBase() {
  return API_BASE;
}
export function setLoginPath(path) {
  LOGIN_PATH = String(path || "/admin/login").startsWith("/") ? path : `/${path}`;
}

/* --------------------------------- Utilities -------------------------------- */
const DEFAULT_TIMEOUT_MS = 20_000;

export class ApiError extends Error {
  constructor(message, status = 0, payload = undefined) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

const isJsonCT = (ct) => typeof ct === "string" && ct.toLowerCase().includes("application/json");

// strip possible XSSI guards and BOM before JSON.parse
function stripJsonNoise(s = "") {
  return String(s)
    .replace(/^\uFEFF/, "")           // BOM
    .replace(/^\)\]\}',?\s*/, "");    // )]}'
}
const safeParseJson = (s) => { try { return JSON.parse(stripJsonNoise(s)); } catch { return {}; } };

// remove undefined keys so we don’t send noisy bodies
function pruneUndefined(obj) {
  if (!obj || typeof obj !== "object") return obj;
  const out = Array.isArray(obj) ? [] : {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined) continue;
    out[k] = v;
  }
  return out;
}

function buildQS(obj = {}) {
  const pairs = [];
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) {
      v.forEach((x) => pairs.push(`${encodeURIComponent(k)}=${encodeURIComponent(x)}`));
    } else {
      pairs.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
    }
  }
  return pairs.join("&");
}

function joinUrl(base, path) {
  const p = String(path || "");
  // Absolute path or fully qualified URL: respect as-is when base is empty
  if (!base) return p;
  if (/^https?:\/\//i.test(p)) return p;
  if (!p.startsWith("/")) return `${base}/${p}`;
  return `${base}${p}`;
}

// Intelligent retry for idempotent GETs
async function withRetries(fn, { retries = 0, baseDelayMs = 250, retryOnStatus = [408, 429, 502, 503, 504] } = {}) {
  let lastErr;
  for (let i = 0; i <= retries; i++) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      const status = Number(e?.status || 0);
      const isNetwork = status === 0; // fetch/network error
      const shouldRetry = i < retries && (isNetwork || retryOnStatus.includes(status));
      if (!shouldRetry) break;
      const jitter = Math.floor(Math.random() * 80);
      await new Promise((r) => setTimeout(r, baseDelayMs * (i + 1) + jitter));
    }
  }
  throw lastErr;
}

/* -------------------------- Unauthorized handling --------------------------- */
const unauthorizedHandlers = new Set();
export function onUnauthorized(handler) {
  if (typeof handler === "function") unauthorizedHandlers.add(handler);
  return () => unauthorizedHandlers.delete(handler);
}

function handleUnauthorizedRedirect() {
  try {
    localStorage.removeItem("adminUser");
    localStorage.removeItem("rememberAdmin");
  } catch {}
  if (typeof window === "undefined") return;
  const current = window.location?.pathname || "/";
  if (current.startsWith(LOGIN_PATH)) return; // avoid loops
  const next = encodeURIComponent(current + window.location.search + window.location.hash);
  window.location.replace(`${LOGIN_PATH}?next=${next}`);
}

/* --------------------------------- Core fetch -------------------------------- */
export async function request(
  path,
  {
    method = "GET",
    data,
    isForm = false,
    query,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    headers = {},
    signal,
    cache,
    retry = 0,            // GET retries
    redirectOn401 = false, // ⬅️ only redirect when explicitly requested (admin-only flows)
    keepalive = method !== "GET", // send even during tab close/nav (useful for POST)
  } = {}
) {
  const urlStr = joinUrl(API_BASE, path || "");
  const qs = query ? buildQS(query) : "";
  const finalUrl = qs ? `${urlStr}${urlStr.includes("?") ? "&" : "?"}${qs}` : urlStr;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const finalSignal = signal ? AbortSignalAny([signal, controller.signal]) : controller.signal;

  // Add a tiny correlation id for server logs
  const reqId = Math.random().toString(36).slice(2);

  const doFetch = async () => {
    const opts = {
      method,
      credentials: "include", // cookie auth (same-origin recommended)
      headers: {
        Accept: "application/json, text/plain, */*",
        "X-Request-Id": reqId,
        ...headers,
      },
      signal: finalSignal,
      cache,         // allow caller to set "no-store"
      keepalive,     // helps ensure POST completes even if page navigates
      referrerPolicy: "no-referrer", // reduce referrer leakage
    };

    if (data !== undefined && !isForm) {
      opts.headers["Content-Type"] = "application/json";
      opts.body = JSON.stringify(pruneUndefined(data));
    } else if (isForm && data instanceof FormData) {
      opts.body = data; // boundary set by browser
    }

    let res;
    try {
      // Offline hint
      if (typeof navigator !== "undefined" && "onLine" in navigator && navigator.onLine === false) {
        throw new ApiError("You appear to be offline.", 0);
      }
      res = await fetch(finalUrl, opts);
    } catch (e) {
      if (e?.name === "AbortError") throw new ApiError("Request timed out", 408);
      // Network error usually indicates CORS mismatch or DNS/connection issues
      throw new ApiError("Network error. Please check your connection.", 0);
    }

    const ct = res.headers.get("content-type") || "";
    const isJSON = isJsonCT(ct);
    const raw = await res.text();
    const body = raw ? (isJSON ? safeParseJson(raw) : raw) : (isJSON ? {} : "");

    // Unauthorized: only redirect when explicitly asked by caller (admin flows)
    if ([401, 419, 440].includes(res.status)) {
      unauthorizedHandlers.forEach((h) => { try { h(res, body); } catch {} });
      if (redirectOn401) handleUnauthorizedRedirect();
      throw new ApiError(body?.message || "Unauthorized", res.status, body);
    }

    if (res.status === 204) return {};
    if (!res.ok) {
      const msg = (isJSON ? body?.message : raw || body) || `HTTP ${res.status}`;
      throw new ApiError(String(msg), res.status, body);
    }
    return isJSON ? body : raw;
  };

  try {
    const out = method === "GET" && retry > 0
      ? await withRetries(doFetch, { retries: retry })
      : await doFetch();
    return out;
  } finally {
    clearTimeout(timeoutId);
  }
}

// Combine multiple AbortSignals
function AbortSignalAny(signals) {
  const ctl = new AbortController();
  const onAbort = () => ctl.abort();
  signals.forEach((s) => {
    if (!s) return;
    if (s.aborted) ctl.abort();
    else s.addEventListener("abort", onAbort, { once: true });
  });
  return ctl.signal;
}

/* ------------------------------ Public ENV (UI) ------------------------------ */
export const PUBLIC_ENV = {
  RAZORPAY_KEY_ID: envGet(["RAZORPAY_KEY_ID"], ""),
  OFFICIAL_WHATSAPP_NUMBER: envGet(["OFFICIAL_WHATSAPP_NUMBER"], ""),
  WHATSAPP_PREFILL_TEMPLATE: envGet(
    ["WHATSAPP_PREFILL_TEMPLATE"],
    "Thank you for donating to Shivansh Mahasabha! Name: {NAME}, Amount: ₹{AMOUNT}, Txn: {PAYMENT_ID}"
  ),
};

/* ----------------------------- Download utilities ---------------------------- */
export function downloadBase64File({ base64, mime = "application/pdf", filename = "file.pdf" }) {
  try {
    const clean = String(base64).replace(/^data:.*;base64,/, "").trim();
    const binary = atob(clean);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: mime || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename || "file";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch {
    const href = `data:${mime};base64,${base64}`;
    const a = document.createElement("a");
    a.href = href; a.download = filename || "file";
    document.body.appendChild(a); a.click(); a.remove();
  }
}

/* --------------------------------- API surface ------------------------------- */
export const api = {
  /* -------------------- Public auth -------------------- */
  auth: {
    me: (opts = {}) => request("/api/auth-me", {
      headers: { "Cache-Control": "no-store" },
      cache: "no-store",
      retry: 1,
      redirectOn401: false, // visitors never redirected
      ...opts,
    }),
    login: (email, password, remember = false) =>
      request("/api/login", { method: "POST", data: { email, password, remember } }),
    logout: () => request("/api/logout", { method: "POST" }),
  },

  /* -------------------- Public membership -------------------- */
  membership: {
    submit: ({ name, phone, aadhaar, city, state }, opts = {}) =>
      request("/api/membership-submit", {
        method: "POST",
        data: { name, phone, aadhaar, city, state },
        redirectOn401: false,
        ...opts,
      }),
    redirectToWhatsApp: (whatsappUrl) => {
      if (typeof window === "undefined") return;
      if (whatsappUrl && /^https:\/\/(wa\.me|api\.whatsapp\.com)\//.test(whatsappUrl)) {
        window.location.href = whatsappUrl;
      }
    },
  },

  /* -------------------- Cloudinary -------------------- */
  cloudinary: {
    signature: (paramsToSign, opts = {}) =>
      request("/api/cloudinary-signature", {
        method: "POST",
        data: { params: paramsToSign },
        redirectOn401: false,
        ...opts,
      }),
  },

  /* -------------------- Gallery -------------------- */
  gallery: {
    create: ({ url, type, public_id, tags }, opts = {}) =>
      request("/api/gallery-create", { method: "POST", data: { url, type, public_id, tags }, redirectOn401: false, ...opts }),
    list: (query, opts = {}) => request("/api/gallery-list", { query, retry: 1, redirectOn401: false, ...opts }),
    remove: ({ public_id, url }, opts = {}) =>
      request("/api/gallery-delete", { method: "POST", data: { public_id, url }, redirectOn401: false, ...opts }),
    update: ({ id, _id, public_id, url, title, caption, type, tags }, opts = {}) =>
      request("/api/gallery-update", { method: "POST", data: { id, _id, public_id, url, title, caption, type, tags }, redirectOn401: false, ...opts }),
  },

  /* -------------------- Newsletter -------------------- */
  newsletter: {
    list: (query, opts = {}) =>
      request("/api/newsletter-list", { query, retry: 1, redirectOn401: false, ...opts })
        .then(r => r?.items || []),

    update: ({ _id, public_id, url, documentLink, title }, opts = {}) =>
      request("/api/newsletter-update", {
        method: "POST",
        data: { _id, public_id, url, documentLink, title },
        redirectOn401: false,
        ...opts,
      }),

    remove: ({ public_id, url }, opts = {}) =>
      request("/api/newsletter-delete", {
        method: "POST",
        data: { public_id, url },
        redirectOn401: false,
        ...opts,
      }),

    // Alias of list, but some code calls "news"
    news: (opts = {}) =>
      request("/api/news-get", { retry: 1, redirectOn401: false, ...opts })
        .then(r => r?.items || []),
  },

  /* -------------------- Payments -------------------- */
  payments: {
    /**
     * Create a Razorpay order.
     * Accepts either:
     *  - number (amount in paise), or
     *  - object payload:
     *    { amount, amountRupees, donorName, donorEmail, donorPhone, purpose, notes }
     */
    createOrder: (payloadOrAmount, opts = {}) => {
      let data;
      if (typeof payloadOrAmount === "number") {
        // prefer explicit rupees on the client; server also accepts paise
        const rupees = Number(payloadOrAmount);
        data = Number.isFinite(rupees) && rupees < 1000
          ? { amountRupees: rupees }
          : { amount: rupees }; // assume paise if large number
      } else if (payloadOrAmount && typeof payloadOrAmount === "object") {
        const { amount, amountRupees, donorName, donorEmail, donorPhone, purpose, notes } = payloadOrAmount;
        data = pruneUndefined({
          amount: Number.isFinite(amount) ? Number(amount) : undefined,
          amountRupees: Number.isFinite(amountRupees) ? Number(amountRupees) : undefined,
          donorName, donorEmail, donorPhone, purpose, notes,
        });
      } else {
        throw new Error("createOrder requires an amount (paise/rupees) or payload object");
      }
      return request("/api/create-order", { method: "POST", data, redirectOn401: false, ...opts });
    },

    /**
     * Verify a Razorpay payment and trigger invoice email.
     * Pass the full payload:
     * {
     *   razorpay_order_id, razorpay_payment_id, razorpay_signature,
     *   donorName, donorEmail, donorPhone,
     *   amount, purpose, notes
     * }
     */
    verify: (payload, opts = {}) =>
      request("/api/verify-payment", { method: "POST", data: payload, redirectOn401: false, ...opts }),
  },

  /* -------------------- Admin-only -------------------- */
  admin: {
    dashboard: (opts = {}) =>
      request("/api/admin/dashboard", { retry: 1, redirectOn401: true, ...opts }), // only admins bounce to /admin/login
  },
};
